import { useCallback } from 'react';
import { debounce, scope } from '~/modules/common/debounce';
import {
  getTransitionUrl,
  formatPutBillInput
} from '~/modules/billing-invoicing/common/util';
import {
  getValidationErrorByUri,
  getValidationErrorByDisplayText
} from '~/modules/common/graphql/errors';

const calculationStatusScope = scope();

export const setDisplayIdError = ({
  error,
  setFieldError,
  intl,
  setCreateAndView,
  setSaving
}) => {
  setCreateAndView(false);
  setSaving(false);
  const displayIdError =
    getValidationErrorByUri(
      error,
      'urn:replicon:validation-failure:invoice-number-is-used-by-another-invoice'
    ) ||
    getValidationErrorByDisplayText(
      error,
      'The specified Invoice already exists.'
    );

  if (displayIdError) {
    setFieldError('displayId', 'duplicateReferenceNumber');
  }
};

export const useAddLineItemsHooks = ({
  createBill,
  validateForm,
  setFieldError,
  setSaving,
  createAndView,
  setCreateAndView,
  billId,
  setSummarizeColumOptions,
  createInvoiceItemsFromBillingItemsBatch,
  createInvoiceFromBillItemsBatch,
  fetchCalculationStatus,
  fetchBatchResults,
  columns,
  values,
  billingItemSearch,
  selectedBillingItems,
  setLineItemAdding,
  onClose,
  refetchAvailableToBillRows,
  refetchAvailableToBillTotal,
  refetchFilteredTotals,
  history,
  params,
  intl,
  putBill,
  billDetails,
  setBillingBatchState,
  setIsBillDataReflectedInAvailableToBillData,
  isPsaPrpBillUiEnhancementsEnabled
}) => {
  const onAddLineItems = useCallback(
    async viewBill => {
      if (createBill) {
        const errors = await validateForm();

        if (Object.keys(errors).length) return;
        const {
          displayId,
          description,
          billingAddress,
          issueDate,
          paymentTerms,
          poNumber,
          period,
          dueDate,
          client: { id: clientUri },
          billCurrency: { id: currencyUri },
          invoiceTemplate,
          notesForCustomer,
          internalNotes,
          showComments,
          taxProfile
        } = values;

        if (viewBill) {
          setCreateAndView(true);
        } else setSaving(true);

        try {
          await createInvoiceFromBillItemsBatch({
            billInput: {
              id: null,
              displayId,
              billingAddress,
              description,
              issueDate,
              paymentTerms,
              poNumber,
              period,
              dueDate,
              clientUri,
              currencyUri,
              taxProfileUri: taxProfile && taxProfile.id,
              invoiceTemplateUri: invoiceTemplate && invoiceTemplate.id,
              lineItems: [],
              summarizeColumn: columns,
              notesForCustomer,
              internalNotes,
              showComments
            },
            billingItemColumnOption: columns,
            billingItemSearch,
            selectedBillingItems
          });
        } catch (error) {
          setDisplayIdError({
            error,
            setFieldError,
            intl,
            setCreateAndView,
            setSaving
          });
        }
      } else {
        if (!isPsaPrpBillUiEnhancementsEnabled) setLineItemAdding(true);
        setSummarizeColumOptions(columns);
        await createInvoiceItemsFromBillingItemsBatch({
          billId,
          billingItemColumnOption: columns,
          billingItemSearch,
          selectedBillingItems
        });
        const formattedPutBillInput = formatPutBillInput(billDetails);

        formattedPutBillInput.summarizeColumn = columns;

        await putBill(formattedPutBillInput);
        if (!isPsaPrpBillUiEnhancementsEnabled) {
          setLineItemAdding(false);
          onClose();
        }
      }
    },
    [
      createBill,
      validateForm,
      values,
      setSaving,
      setCreateAndView,
      createInvoiceFromBillItemsBatch,
      columns,
      billingItemSearch,
      selectedBillingItems,
      setFieldError,
      intl,
      setLineItemAdding,
      setSummarizeColumOptions,
      createInvoiceItemsFromBillingItemsBatch,
      billId,
      billDetails,
      putBill,
      onClose,
      isPsaPrpBillUiEnhancementsEnabled
    ]
  );

  const refetchAvailableToBillData = useCallback(async () => {
    if (isPsaPrpBillUiEnhancementsEnabled) {
      await Promise.all([
        refetchAvailableToBillRows?.(),
        refetchAvailableToBillTotal?.(),
        refetchFilteredTotals?.()
      ]);
    } else {
      if (refetchAvailableToBillRows) await refetchAvailableToBillRows();
      if (refetchAvailableToBillTotal) await refetchAvailableToBillTotal();
      if (refetchFilteredTotals) await refetchFilteredTotals();
    }
  }, [
    isPsaPrpBillUiEnhancementsEnabled,
    refetchAvailableToBillRows,
    refetchAvailableToBillTotal,
    refetchFilteredTotals
  ]);

  const onCalculationComplete = useCallback(
    async ({ bill, viewBill }) => {
      const response = await fetchCalculationStatus({ billId: bill });
      const status = response?.data?.isBillDataReflectedInAvailableToBillData;

      if (status || !bill) {
        await refetchAvailableToBillData();
        setSaving(false);
        setCreateAndView(false);
        if (isPsaPrpBillUiEnhancementsEnabled)
          setIsBillDataReflectedInAvailableToBillData(false);

        onClose();
        if (viewBill) {
          const record = { id: bill, type: 'BILL' };
          const path = history.location.pathname;
          const transitionUrl = getTransitionUrl({
            path,
            record,
            slug: params.slug
          });

          history.push(transitionUrl);
        }
      } else {
        await debounce(
          calculationStatusScope,
          async () => {
            await onCalculationComplete({ bill, viewBill });
          },
          500
        );
      }
    },
    [
      fetchCalculationStatus,
      refetchAvailableToBillData,
      setSaving,
      setCreateAndView,
      isPsaPrpBillUiEnhancementsEnabled,
      setIsBillDataReflectedInAvailableToBillData,
      onClose,
      history,
      params
    ]
  );

  const onBatchComplete = useCallback(
    async ({ batchId = {} }) => {
      if (createBill && refetchAvailableToBillRows) {
        const result = await fetchBatchResults({ batchId });

        const {
          data: {
            getCreateBillFromBillingItemsBatchResults: { billId: bill } = {}
          } = {}
        } = result;

        await onCalculationComplete({ bill, viewBill: false });
      }
    },
    [
      createBill,
      refetchAvailableToBillRows,
      fetchBatchResults,
      onCalculationComplete
    ]
  );

  const onBatchCompleteViewBill = useCallback(
    async ({ batchId = {} }) => {
      if (createBill && refetchAvailableToBillRows) {
        const result = await fetchBatchResults({ batchId });

        const {
          data: {
            getCreateBillFromBillingItemsBatchResults: { billId: bill } = {}
          } = {}
        } = result;

        await onCalculationComplete({ bill, viewBill: true });
      }
    },
    [
      createBill,
      refetchAvailableToBillRows,
      fetchBatchResults,
      onCalculationComplete
    ]
  );

  const onBatchComplete2 = useCallback(
    async ({ batchId = {} }) => {
      if (createBill && refetchAvailableToBillRows) {
        const result = await fetchBatchResults({ batchId });

        const {
          data: {
            getCreateBillFromBillingItemsBatchResults: { billId: bill } = {}
          } = {}
        } = result;

        setIsBillDataReflectedInAvailableToBillData(true);
        setBillingBatchState({
          batchId: null,
          batchInProgress: false
        });
        await onCalculationComplete({ bill, viewBill: createAndView });

        return undefined;
      }
      if (isPsaPrpBillUiEnhancementsEnabled) {
        const timeoutId = setTimeout(() => {
          onClose();
          setBillingBatchState({
            batchId: null,
            batchInProgress: false
          });
        }, 500);

        return () => clearTimeout(timeoutId);
      }

      return undefined;
    },
    [
      createBill,
      refetchAvailableToBillRows,
      isPsaPrpBillUiEnhancementsEnabled,
      fetchBatchResults,
      setIsBillDataReflectedInAvailableToBillData,
      setBillingBatchState,
      onCalculationComplete,
      createAndView,
      onClose
    ]
  );

  return {
    onAddLineItems,
    onBatchComplete,
    onBatchComplete2,
    onBatchCompleteViewBill
  };
};

export default useAddLineItemsHooks;
